import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import {
    bootstrapPlApp,
    createPassword,
    createPost,
    deletePlProfile,
    decideAccessRequest,
    deleteComment,
    deletePost,
    fetchPlPosts,
    fetchAccessRequests,
    loginWithPassword,
    logoutPlUser,
    requestAccess,
    saveComment,
    setVisiblePlPostCount,
    toggleCommentReaction,
    toggleThreadSubscription,
    toggleToastReaction,
    toggleUserSubscription,
    updateNotificationPreferences,
    updatePost,
} from "./mockPlApi";
import styles from "./PL.module.css";
import beerMark from "./beerMark.svg";

const formatTimestamp = (value) =>
    new Date(value).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });

const emptyAccessRequest = {
    name: "",
    email: "",
    username: "",
    note: "",
};

const emptyLogin = {
    identifier: "",
    password: "",
};

const emptyPasswordSetup = {
    password: "",
    confirmPassword: "",
};

const emptyPost = {
    title: "",
    caption: "",
};

const COMMENT_PAGE_SIZE = 3;
const POST_PAGE_SIZE = 10;
const LONG_PRESS_MS = 450;
const REACTION_TYPES = {
    cheers: { label: "Cheers", icon: "🍻" },
    like: { label: "Like", icon: "👍" },
    heart: { label: "Heart", icon: "❤️" },
    laugh: { label: "Laugh", icon: "😂" },
    cry: { label: "Cry", icon: "😭" },
    angry: { label: "Angry", icon: "😡" },
};

const PLApp = () => {
    const location = useLocation();
    const setupToken = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
    const initialMode = setupToken ? "create-password" : "login";

    const [authMode, setAuthMode] = useState(initialMode);
    const [accessRequestForm, setAccessRequestForm] = useState(emptyAccessRequest);
    const [loginForm, setLoginForm] = useState(emptyLogin);
    const [passwordSetupForm, setPasswordSetupForm] = useState(emptyPasswordSetup);
    const [postForm, setPostForm] = useState(emptyPost);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedPreviewUrl, setSelectedPreviewUrl] = useState("");
    const [currentUser, setCurrentUser] = useState(null);
    const [posts, setPosts] = useState([]);
    const [postPagination, setPostPagination] = useState({
        offset: 0,
        limit: POST_PAGE_SIZE,
        hasMore: false,
        total: 0,
    });
    const [accessRequests, setAccessRequests] = useState([]);
    const [statusMessage, setStatusMessage] = useState("");
    const [statusTone, setStatusTone] = useState("error");
    const [editingPostId, setEditingPostId] = useState(null);
    const [postDrafts, setPostDrafts] = useState({});
    const [deleteProfilePostsConfirmed, setDeleteProfilePostsConfirmed] = useState(false);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [openPostMenuId, setOpenPostMenuId] = useState(null);
    const [openCommentMenuId, setOpenCommentMenuId] = useState(null);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [commentDrafts, setCommentDrafts] = useState({});
    const [expandedComments, setExpandedComments] = useState({});
    const [expandedCheers, setExpandedCheers] = useState({});
    const [expandedCommentReactions, setExpandedCommentReactions] = useState({});
    const [openReactionPickerPostId, setOpenReactionPickerPostId] = useState(null);
    const [openCommentReactionPickerId, setOpenCommentReactionPickerId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const longPressTimerRef = useRef(null);
    const longPressTriggeredRef = useRef(false);
    const reactionPickerRef = useRef(null);
    const feedSentinelRef = useRef(null);
    const accountMenuRef = useRef(null);
    const postMenuRef = useRef(null);
    const commentMenuRef = useRef(null);

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                const state = await bootstrapPlApp({ limit: POST_PAGE_SIZE });
                if (!active) {
                    return;
                }

                setCurrentUser(state.currentUser);
                setPosts(state.posts || []);
                setPostPagination(
                    state.pagination || {
                        offset: (state.posts || []).length,
                        limit: POST_PAGE_SIZE,
                        hasMore: false,
                        total: (state.posts || []).length,
                    }
                );
                setIsLoading(false);
            } catch (error) {
                if (!active) {
                    return;
                }

                setIsLoading(false);
                showStatusMessage(error.message || "Unable to load /pl.", "error");
            }
        };

        load();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        setAuthMode(setupToken ? "create-password" : "login");
    }, [setupToken]);

    useEffect(() => {
        if (!currentUser || !currentUser.isAdmin) {
            setAccessRequests([]);
            return;
        }

        let active = true;

        const loadRequests = async () => {
            try {
                const state = await fetchAccessRequests();
                if (!active) {
                    return;
                }

                setAccessRequests(state.accessRequests || []);
            } catch (error) {
                if (!active) {
                    return;
                }

                showStatusMessage(error.message || "Unable to load access requests.", "error");
            }
        };

        loadRequests();

        return () => {
            active = false;
        };
    }, [currentUser]);

    useEffect(() => {
        if (!selectedFile) {
            setSelectedPreviewUrl("");
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFile);
        setSelectedPreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [selectedFile]);

    useEffect(() => {
        setVisiblePlPostCount(Math.max(posts.length, POST_PAGE_SIZE));
    }, [posts.length]);

    useEffect(() => {
        if (openReactionPickerPostId === null && openCommentReactionPickerId === null) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!reactionPickerRef.current) {
                return;
            }

            if (!reactionPickerRef.current.contains(event.target)) {
                setOpenReactionPickerPostId(null);
                clearLongPressTimer();
                longPressTriggeredRef.current = false;
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [openReactionPickerPostId, openCommentReactionPickerId]);

    useEffect(() => {
        if (!isAccountMenuOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!accountMenuRef.current) {
                return;
            }

            if (!accountMenuRef.current.contains(event.target)) {
                setIsAccountMenuOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [isAccountMenuOpen]);

    useEffect(() => {
        if (openPostMenuId === null) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!postMenuRef.current) {
                return;
            }

            if (!postMenuRef.current.contains(event.target)) {
                setOpenPostMenuId(null);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [openPostMenuId]);

    useEffect(() => {
        if (openCommentMenuId === null) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (!commentMenuRef.current) {
                return;
            }

            if (!commentMenuRef.current.contains(event.target)) {
                setOpenCommentMenuId(null);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, [openCommentMenuId]);

    const showStatusMessage = (message, tone = "success") => {
        setStatusMessage(message);
        setStatusTone(tone);
    };

    const applyState = (state) => {
        if (state.currentUser !== undefined) {
            setCurrentUser(state.currentUser);
        }

        if (state.posts) {
            setPosts(state.posts);
            setPostPagination((prev) => ({
                offset: state.posts.length,
                limit: prev.limit || POST_PAGE_SIZE,
                hasMore: false,
                total: state.posts.length,
            }));
        }

        if (state.pagination) {
            setPostPagination((prev) => ({
                ...prev,
                ...state.pagination,
            }));
        }

        if (state.accessRequests) {
            setAccessRequests(state.accessRequests);
        }
    };

    const loadMorePosts = async () => {
        if (!currentUser || isLoadingMorePosts || !postPagination.hasMore) {
            return;
        }

        setIsLoadingMorePosts(true);

        try {
            const state = await fetchPlPosts({
                offset: posts.length,
                limit: postPagination.limit || POST_PAGE_SIZE,
            });

            setPosts((prev) => [...prev, ...(state.posts || [])]);
            if (state.pagination) {
                setPostPagination((prev) => ({
                    ...prev,
                    ...state.pagination,
                }));
            }
        } catch (error) {
            showStatusMessage(error.message || "Unable to load more posts.", "error");
        } finally {
            setIsLoadingMorePosts(false);
        }
    };

    useEffect(() => {
        if (!currentUser || !postPagination.hasMore || !feedSentinelRef.current) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        loadMorePosts();
                    }
                });
            },
            {
                root: null,
                rootMargin: "240px 0px",
                threshold: 0.01,
            }
        );

        observer.observe(feedSentinelRef.current);

        return () => {
            observer.disconnect();
        };
    }, [currentUser, postPagination.hasMore, posts.length, isLoadingMorePosts]);

    const runAction = async (action, successMessage) => {
        setIsSubmitting(true);

        try {
            const result = await action();
            applyState(result || {});

            if (successMessage) {
                showStatusMessage(successMessage, "success");
            }
        } catch (error) {
            showStatusMessage(error.message || "Something went wrong.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAccessRequest = async (event) => {
        event.preventDefault();
        await runAction(
            () => requestAccess(accessRequestForm),
            "Access request sent. Once approved, you’ll receive an email to create your password."
        );
        setAccessRequestForm(emptyAccessRequest);
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        await runAction(() => loginWithPassword(loginForm), "Signed in.");
        setLoginForm(emptyLogin);
    };

    const handleCreatePassword = async (event) => {
        event.preventDefault();

        if (passwordSetupForm.password !== passwordSetupForm.confirmPassword) {
            showStatusMessage("Passwords do not match.", "error");
            return;
        }

        await runAction(
            () =>
                createPassword({
                    token: setupToken,
                    password: passwordSetupForm.password,
                }),
            "Password created. You can log in now."
        );

        setPasswordSetupForm(emptyPasswordSetup);
        setAuthMode("login");
    };

    const handleLogout = async () => {
        await runAction(() => logoutPlUser(), "Signed out.");
    };

    const handleCreatePost = async (event) => {
        event.preventDefault();

        if (!postForm.title.trim()) {
            showStatusMessage("A title is required before publishing.", "error");
            return;
        }

        if (!selectedFile) {
            showStatusMessage("Choose an image or video before publishing.", "error");
            return;
        }

        setIsSubmitting(true);
        setUploadProgress(0);

        try {
            const result = await createPost({
                title: postForm.title,
                caption: postForm.caption,
                file: selectedFile,
                onUploadProgress: setUploadProgress,
            });

            applyState(result || {});
            showStatusMessage("Post published.", "success");
            setPostForm(emptyPost);
            setSelectedFile(null);
        } catch (error) {
            showStatusMessage(error.message || "Something went wrong.", "error");
        } finally {
            setIsSubmitting(false);
            setUploadProgress(null);
        }
    };

    const handleEditPostStart = (post) => {
        setOpenPostMenuId(null);
        setEditingPostId(post.id);
        setPostDrafts((prev) => ({
            ...prev,
            [post.id]: {
                title: post.title || "",
                caption: post.caption || "",
            },
        }));
    };

    const handleUpdatePost = async (postId) => {
        const draft = postDrafts[postId] || { title: "", caption: "" };
        const title = String(draft.title || "").trim();

        if (!title) {
            showStatusMessage("A title is required before saving a post.", "error");
            return;
        }

        await runAction(
            () => updatePost({ postId, title, caption: draft.caption || "" }),
            "Post updated."
        );
        setEditingPostId(null);
    };

    const handleDeleteProfile = async () => {
        if (!deleteProfilePostsConfirmed) {
            showStatusMessage("Confirm post deletion before deleting your profile.", "error");
            return;
        }

        await runAction(
            () => deletePlProfile({ deletePosts: true }),
            "Your profile has been deleted."
        );
        setDeleteProfilePostsConfirmed(false);
        setIsAccountMenuOpen(false);
    };

    const submitComment = async (postId, commentId = null) => {
        const draftKey = commentId || postId;
        const body = (commentDrafts[draftKey] || "").trim();

        if (!body) {
            return;
        }

        await runAction(
            () => saveComment({ postId, commentId, body }),
            commentId ? "Comment updated." : "Comment added."
        );

        setCommentDrafts((prev) => ({ ...prev, [draftKey]: "" }));
        setEditingCommentId(null);
    };

    const clearLongPressTimer = () => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const startPostReactionHold = (postId) => {
        clearLongPressTimer();
        longPressTriggeredRef.current = false;
        longPressTimerRef.current = window.setTimeout(() => {
            longPressTriggeredRef.current = true;
            setOpenReactionPickerPostId(postId);
            setOpenCommentReactionPickerId(null);
        }, LONG_PRESS_MS);
    };

    const startCommentReactionHold = (commentId) => {
        clearLongPressTimer();
        longPressTriggeredRef.current = false;
        longPressTimerRef.current = window.setTimeout(() => {
            longPressTriggeredRef.current = true;
            setOpenCommentReactionPickerId(commentId);
            setOpenReactionPickerPostId(null);
        }, LONG_PRESS_MS);
    };

    const cancelReactionHold = () => {
        clearLongPressTimer();
    };

    const handleReactionButtonClick = async (postId) => {
        if (longPressTriggeredRef.current) {
            longPressTriggeredRef.current = false;
            return;
        }

        setOpenReactionPickerPostId(null);
        await runAction(
            () => toggleToastReaction({ postId, type: "cheers" }),
            "Reaction updated."
        );
    };

    const handleReactionSelect = async (postId, type) => {
        setOpenReactionPickerPostId(null);
        await runAction(
            () => toggleToastReaction({ postId, type }),
            "Reaction updated."
        );
    };

    const handleCommentReactionButtonClick = async (postId, commentId) => {
        if (longPressTriggeredRef.current) {
            longPressTriggeredRef.current = false;
            return;
        }

        setOpenCommentReactionPickerId(null);
        await runAction(
            () => toggleCommentReaction({ postId, commentId, type: "cheers" }),
            "Reaction updated."
        );
    };

    const handleCommentReactionSelect = async (postId, commentId, type) => {
        setOpenCommentReactionPickerId(null);
        await runAction(
            () => toggleCommentReaction({ postId, commentId, type }),
            "Reaction updated."
        );
    };

    const getReactionIcons = (reactions) =>
        Array.from(
            new Set(
                (reactions || []).map(
                    (reaction) => (REACTION_TYPES[reaction.type] || REACTION_TYPES.cheers).icon
                )
            )
        ).slice(0, 5);

    const handleDecision = async (requestId, decision) => {
        setIsSubmitting(true);

        try {
            const result = await decideAccessRequest({ requestId, decision });
            applyState(result || {});

            showStatusMessage(decision === "approved" ? "Access approved." : "Access denied.", "success");
        } catch (error) {
            showStatusMessage(error.message || "Something went wrong.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubscriptionToggle = async (userId, isSubscribed) => {
        await runAction(
            () => toggleUserSubscription({ userId }),
            isSubscribed ? "Subscription removed." : "Subscription updated."
        );
    };

    const handleThreadSubscriptionToggle = async (postId, isSubscribed) => {
        await runAction(
            () => toggleThreadSubscription({ postId }),
            isSubscribed ? "Post subscription removed." : "Post subscription updated."
        );
    };

    const handleNotificationPreferenceChange = async (patch) => {
        const nextPreferences = {
            notifyNewPosts: currentUser.notifyNewPosts !== false,
            notifyCommentsOnPosts: currentUser.notifyCommentsOnPosts !== false,
            ...patch,
        };

        await runAction(
            () => updateNotificationPreferences(nextPreferences),
            "Notification preferences updated."
        );
    };

    if (isLoading) {
        return (
            <div className={styles.page}>
                <Helmet>
                    <title>/pl</title>
                    <link rel="canonical" href="https://rjbogz.com/pl" />
                    <link rel="manifest" href="/pl-manifest.json" />
                    <link rel="icon" type="image/x-icon" href="/logo.ico" />
                    <link rel="apple-touch-icon" href="/logo.ico" />
                    <meta name="apple-mobile-web-app-capable" content="yes" />
                    <meta name="mobile-web-app-capable" content="yes" />
                    <meta name="apple-mobile-web-app-title" content="/pl" />
                    <meta name="application-name" content="/pl" />
                </Helmet>
                Loading /pl...
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className={styles.page}>
                <Helmet>
                    <title>/pl</title>
                    <link rel="canonical" href="https://rjbogz.com/pl" />
                    <link rel="manifest" href="/pl-manifest.json" />
                    <link rel="icon" type="image/x-icon" href="/logo.ico" />
                    <link rel="apple-touch-icon" href="/logo.ico" />
                    <meta name="apple-mobile-web-app-capable" content="yes" />
                    <meta name="mobile-web-app-capable" content="yes" />
                    <meta name="apple-mobile-web-app-title" content="/pl" />
                    <meta name="application-name" content="/pl" />
                </Helmet>
                <div className={styles.gateShell}>
                    <aside className={styles.authPanel}>
                        <div className={styles.gateBadge}>
                            <img src={beerMark} alt="PL beer mark" className={styles.gateLogo} />
                            <span>/pl</span>
                        </div>

                        <div className={styles.authTabs}>
                            <button
                                type="button"
                                className={authMode === "login" ? styles.authTabActive : ""}
                                onClick={() => setAuthMode("login")}
                            >
                                Log in
                            </button>
                            <button
                                type="button"
                                className={authMode === "request" ? styles.authTabActive : ""}
                                onClick={() => setAuthMode("request")}
                            >
                                Request access
                            </button>
                            {setupToken && (
                                <button
                                    type="button"
                                    className={authMode === "create-password" ? styles.authTabActive : ""}
                                    onClick={() => setAuthMode("create-password")}
                                >
                                    Create password
                                </button>
                            )}
                        </div>

                        {authMode === "login" && (
                            <form className={styles.authForm} onSubmit={handleLogin}>
                                <h2>Log in</h2>
                                <input
                                    className={styles.field}
                                    placeholder="Username or email"
                                    value={loginForm.identifier}
                                    onChange={(event) =>
                                        setLoginForm((prev) => ({ ...prev, identifier: event.target.value }))
                                    }
                                />
                                <input
                                    className={styles.field}
                                    type="password"
                                    placeholder="Password"
                                    value={loginForm.password}
                                    onChange={(event) =>
                                        setLoginForm((prev) => ({ ...prev, password: event.target.value }))
                                    }
                                />
                                <button className={styles.primaryAction} type="submit" disabled={isSubmitting}>
                                    Log in
                                </button>
                            </form>
                        )}

                        {authMode === "request" && (
                            <form className={styles.authForm} onSubmit={handleAccessRequest}>
                                <h2>Request access</h2>
                                <input
                                    className={styles.field}
                                    placeholder="Name"
                                    value={accessRequestForm.name}
                                    onChange={(event) =>
                                        setAccessRequestForm((prev) => ({ ...prev, name: event.target.value }))
                                    }
                                />
                                <input
                                    className={styles.field}
                                    type="email"
                                    placeholder="Email"
                                    value={accessRequestForm.email}
                                    onChange={(event) =>
                                        setAccessRequestForm((prev) => ({ ...prev, email: event.target.value }))
                                    }
                                />
                                <input
                                    className={styles.field}
                                    placeholder="Requested username"
                                    value={accessRequestForm.username}
                                    onChange={(event) =>
                                        setAccessRequestForm((prev) => ({ ...prev, username: event.target.value }))
                                    }
                                />
                                <textarea
                                    className={styles.fieldArea}
                                    placeholder="Optional note"
                                    value={accessRequestForm.note}
                                    onChange={(event) =>
                                        setAccessRequestForm((prev) => ({ ...prev, note: event.target.value }))
                                    }
                                />
                                <button className={styles.primaryAction} type="submit" disabled={isSubmitting}>
                                    Send request
                                </button>
                            </form>
                        )}

                        {authMode === "create-password" && (
                            <form className={styles.authForm} onSubmit={handleCreatePassword}>
                                <h2>Create password</h2>
                                <input
                                    className={styles.field}
                                    type="password"
                                    placeholder="New password"
                                    value={passwordSetupForm.password}
                                    onChange={(event) =>
                                        setPasswordSetupForm((prev) => ({ ...prev, password: event.target.value }))
                                    }
                                />
                                <input
                                    className={styles.field}
                                    type="password"
                                    placeholder="Confirm password"
                                    value={passwordSetupForm.confirmPassword}
                                    onChange={(event) =>
                                        setPasswordSetupForm((prev) => ({
                                            ...prev,
                                            confirmPassword: event.target.value,
                                        }))
                                    }
                                />
                                <button className={styles.primaryAction} type="submit" disabled={isSubmitting}>
                                    Save password
                                </button>
                            </form>
                        )}

                        <p className={styles.helperCopy}>
                            Sign-in is required before any posts are visible.
                        </p>
                        {statusMessage && (
                            <div className={statusTone === "success" ? styles.statusMessageSuccess : styles.authMessage}>
                                {statusMessage}
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Helmet>
                <title>/pl</title>
                <link rel="canonical" href="https://rjbogz.com/pl" />
                <link rel="manifest" href="/pl-manifest.json" />
                <link rel="icon" type="image/x-icon" href="/logo.ico" />
                <link rel="apple-touch-icon" href="/logo.ico" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-title" content="/pl" />
                <meta name="application-name" content="/pl" />
            </Helmet>
            <div className={styles.appShell}>
                <header className={styles.topbar}>
                    <div className={styles.topbarSpacer} />
                    <div className={styles.brandMark}>
                        <img src={beerMark} alt="PL beer mark" className={styles.brandLogo} />
                    </div>
                    <div className={styles.topbarActions}>
                        {currentUser.isAdmin && <span className={styles.roleBadge}>Admin</span>}
                        <div className={styles.accountMenuWrap} ref={accountMenuRef}>
                            <button
                                type="button"
                                className={styles.accountMenuButton}
                                onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                            >
                                {currentUser.username}
                            </button>
                            {isAccountMenuOpen && (
                                <div className={styles.accountMenu}>
                                    <div className={styles.menuSection}>
                                        <strong>Notifications</strong>
                                        <label className={styles.checkboxRow}>
                                            <input
                                                type="checkbox"
                                                checked={currentUser.notifyNewPosts !== false}
                                                onChange={(event) =>
                                                    handleNotificationPreferenceChange({
                                                        notifyNewPosts: event.target.checked,
                                                    })
                                                }
                                            />
                                            <span>New video posts from people I subscribe to</span>
                                        </label>
                                        <label className={styles.checkboxRow}>
                                            <input
                                                type="checkbox"
                                                checked={currentUser.notifyCommentsOnPosts !== false}
                                                onChange={(event) =>
                                                    handleNotificationPreferenceChange({
                                                        notifyCommentsOnPosts: event.target.checked,
                                                    })
                                                }
                                            />
                                            <span>Activity on posts I'm subscribed to</span>
                                        </label>
                                    </div>
                                    <label className={styles.checkboxRow}>
                                        <input
                                            type="checkbox"
                                            checked={deleteProfilePostsConfirmed}
                                            onChange={(event) => setDeleteProfilePostsConfirmed(event.target.checked)}
                                        />
                                        <span>Delete all of my posts with my profile.</span>
                                    </label>
                                    <button
                                        type="button"
                                        className={styles.dangerAction}
                                        disabled={!deleteProfilePostsConfirmed || isSubmitting}
                                        onClick={handleDeleteProfile}
                                    >
                                        Delete profile
                                    </button>
                                    <button type="button" className={styles.secondaryAction} onClick={handleLogout}>
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className={styles.content}>
                    {currentUser.isAdmin && accessRequests.length > 0 && (
                        <section className={styles.composer}>
                            <div className={styles.composerHeader}>
                                <div>
                                    <h3 className={styles.sectionTitle}>Access requests</h3>
                                    <p className={styles.sectionText}>Approve or deny signups directly from the site.</p>
                                </div>
                            </div>

                            <div className={styles.itemList}>
                                {accessRequests.map((request) => (
                                    <div key={request.id} className={styles.itemCard}>
                                        <div className={styles.itemMeta}>
                                            <strong>{request.username}</strong>
                                            <span>{request.email}</span>
                                            <span>{request.status}</span>
                                            <span>{formatTimestamp(request.requestedAt || request.requested_at)}</span>
                                        </div>
                                        {request.note && <p className={styles.itemBody}>{request.note}</p>}
                                        {request.status === "pending" && (
                                            <div className={styles.itemActions}>
                                                <button
                                                    type="button"
                                                    className={styles.secondaryAction}
                                                    onClick={() => handleDecision(request.id, "approved")}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.dangerAction}
                                                    onClick={() => handleDecision(request.id, "denied")}
                                                >
                                                    Deny
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    <section className={styles.composer}>
                        <div className={styles.composerHeader}>
                            <div>
                                <h3 className={styles.sectionTitle}>Post something</h3>
                                <p className={styles.sectionText}>
                                    Add a title and an image or video. Captions are optional.
                                </p>
                            </div>
                        </div>
                        <form className={styles.formGrid} onSubmit={handleCreatePost}>
                            <input
                                className={styles.field}
                                placeholder="Title"
                                value={postForm.title}
                                onChange={(event) => setPostForm((prev) => ({ ...prev, title: event.target.value }))}
                            />
                            <textarea
                                className={styles.fieldArea}
                                placeholder="Caption"
                                value={postForm.caption}
                                onChange={(event) => setPostForm((prev) => ({ ...prev, caption: event.target.value }))}
                            />
                            <input
                                className={styles.field}
                                type="file"
                                accept="image/*,video/*"
                                onChange={(event) =>
                                    setSelectedFile(event.target.files && event.target.files[0] ? event.target.files[0] : null)
                                }
                            />
                            {selectedFile && (
                                <div className={styles.previewCard}>
                                    <strong>{selectedFile.name}</strong>
                                    <div className={styles.helperText}>{selectedFile.type || "Unknown file type"}</div>
                                    {selectedPreviewUrl && selectedFile.type.startsWith("image/") && (
                                        <img className={styles.postMedia} src={selectedPreviewUrl} alt="Upload preview" />
                                    )}
                                    {selectedPreviewUrl && selectedFile.type.startsWith("video/") && (
                                        <video className={styles.postVideo} src={selectedPreviewUrl} controls />
                                    )}
                                </div>
                            )}
                            <button className={styles.primaryAction} type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Uploading..." : "Publish post"}
                            </button>
                        </form>
                        {uploadProgress !== null && (
                            <div className={styles.uploadProgressCard}>
                                <div className={styles.uploadProgressMeta}>
                                    <strong>Uploading media</strong>
                                    <span>{uploadProgress}%</span>
                                </div>
                                <div className={styles.uploadProgressTrack} aria-hidden="true">
                                    <div
                                        className={styles.uploadProgressFill}
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </section>

                    <section className={styles.feed}>
                        {statusMessage && (
                            <div className={statusTone === "success" ? styles.statusMessageSuccess : styles.statusMessage}>
                                {statusMessage}
                            </div>
                        )}
                        {posts.length === 0 && <div className={styles.emptyState}>No posts yet. Create the first one.</div>}

                        {posts.map((post) => {
                            const userReaction = (post.toasts || []).find(
                                (toast) => toast.author && toast.author.id === currentUser.id
                            );
                            const userReacted = Boolean(userReaction);
                            const cheersExpanded = Boolean(expandedCheers[post.id]);
                            const visibleCommentCount = expandedComments[post.id] || COMMENT_PAGE_SIZE;
                            const comments = post.comments || [];
                            const hiddenCommentCount = Math.max(0, comments.length - visibleCommentCount);
                            const canCollapseComments = visibleCommentCount > COMMENT_PAGE_SIZE;
                            const visibleComments =
                                comments.length <= visibleCommentCount
                                    ? comments
                                    : comments.slice(-visibleCommentCount);
                            const pickerOpen = openReactionPickerPostId === post.id;
                            const canEditPost =
                                currentUser &&
                                post.author &&
                                currentUser.id === post.author.id;
                            const postMenuOpen = openPostMenuId === post.id;
                            const reactionButtonIcon = userReaction
                                ? (REACTION_TYPES[userReaction.type] || REACTION_TYPES.cheers).icon
                                : REACTION_TYPES.cheers.icon;
                            const reactionIcons = getReactionIcons(post.toasts || []);

                            return (
                                <article key={post.id} id={`post-${post.id}`} className={styles.postCard}>
                                    <div className={styles.postHeader}>
                                        <div>
                                            <div className={styles.authorChip}>
                                                <span>{post.author ? post.author.username : "Unknown user"}</span>
                                            </div>
                                            {post.author && currentUser && post.author.id !== currentUser.id && (
                                                <div className={styles.authorSubscriptionRow}>
                                                    <button
                                                        type="button"
                                                        className={post.author.isSubscribed ? styles.secondaryAction : styles.ghostAction}
                                                        onClick={() =>
                                                            handleSubscriptionToggle(post.author.id, post.author.isSubscribed)
                                                        }
                                                    >
                                                        {post.author.isSubscribed ? "Subscribed" : "Subscribe"}
                                                    </button>
                                                    <span className={styles.metaText}>
                                                        {post.author.subscriberCount || 0} subscriber
                                                        {(post.author.subscriberCount || 0) === 1 ? "" : "s"}
                                                    </span>
                                                </div>
                                            )}
                                            <h3 className={styles.postTitle}>{post.title || "Untitled post"}</h3>
                                            <p className={styles.metaText}>
                                                Posted {formatTimestamp(post.createdAt)}
                                                {post.updatedAt !== post.createdAt
                                                    ? ` • Updated ${formatTimestamp(post.updatedAt)}`
                                                    : ""}
                                            </p>
                                        </div>
                                        {currentUser && (
                                            <div className={styles.postMenuWrap} ref={postMenuOpen ? postMenuRef : null}>
                                                <button
                                                    type="button"
                                                    className={styles.postMenuButton}
                                                    aria-label="Post actions"
                                                    onClick={() =>
                                                        setOpenPostMenuId((prev) => (prev === post.id ? null : post.id))
                                                    }
                                                >
                                                    ...
                                                </button>
                                                {postMenuOpen && (
                                                    <div className={styles.postMenu}>
                                                        <button
                                                            type="button"
                                                            className={styles.postMenuItem}
                                                            onClick={() => {
                                                                setOpenPostMenuId(null);
                                                                handleThreadSubscriptionToggle(post.id, post.isThreadSubscribed);
                                                            }}
                                                        >
                                                            {post.isThreadSubscribed ? "Unsubscribe from post" : "Subscribe to post"}
                                                        </button>
                                                        {canEditPost && editingPostId !== post.id && (
                                                            <button
                                                                type="button"
                                                                className={styles.postMenuItem}
                                                                onClick={() => handleEditPostStart(post)}
                                                            >
                                                                Edit post
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className={`${styles.postMenuItem} ${styles.postMenuDanger}`}
                                                            onClick={() => {
                                                                setOpenPostMenuId(null);
                                                                runAction(() => deletePost({ postId: post.id }), "Post deleted.");
                                                            }}
                                                        >
                                                            Delete post
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {canEditPost && editingPostId === post.id ? (
                                        <div className={styles.formGrid}>
                                            <input
                                                className={styles.field}
                                                value={(postDrafts[post.id] && postDrafts[post.id].title) || ""}
                                                onChange={(event) =>
                                                    setPostDrafts((prev) => ({
                                                        ...prev,
                                                        [post.id]: {
                                                            ...(prev[post.id] || {}),
                                                            title: event.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                            <textarea
                                                className={styles.fieldArea}
                                                value={(postDrafts[post.id] && postDrafts[post.id].caption) || ""}
                                                onChange={(event) =>
                                                    setPostDrafts((prev) => ({
                                                        ...prev,
                                                        [post.id]: {
                                                            ...(prev[post.id] || {}),
                                                            caption: event.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                            <div className={styles.formRow}>
                                                <button
                                                    type="button"
                                                    className={styles.secondaryAction}
                                                    onClick={() => handleUpdatePost(post.id)}
                                                >
                                                    Save post
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.ghostAction}
                                                    onClick={() => setEditingPostId(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        post.caption && <p className={styles.postCaption}>{post.caption}</p>
                                    )}
                                    {post.mediaUrl && post.mediaType === "image" && (
                                        <img className={styles.postMedia} src={post.mediaUrl} alt={post.title || "Post"} />
                                    )}
                                    {post.mediaUrl && post.mediaType === "video" && (
                                        <video className={styles.postVideo} src={post.mediaUrl} controls />
                                    )}

                                    <div className={styles.postFooter}>
                                        <section className={`${styles.interactionPanel} ${styles.cheersPanel}`}>
                                            <div className={styles.reactionControl} ref={pickerOpen ? reactionPickerRef : null}>
                                                <button
                                                    type="button"
                                                    className={`${styles.reactionToggle} ${userReacted ? styles.reactionToggleActive : ""}`}
                                                    aria-label={userReacted ? "Update reaction" : "Add cheers reaction"}
                                                    title="Tap for cheers, hold for more reactions"
                                                    onMouseDown={() => startPostReactionHold(post.id)}
                                                    onMouseUp={cancelReactionHold}
                                                    onMouseLeave={cancelReactionHold}
                                                    onTouchStart={() => startPostReactionHold(post.id)}
                                                    onTouchEnd={cancelReactionHold}
                                                    onTouchCancel={cancelReactionHold}
                                                    onClick={() => handleReactionButtonClick(post.id)}
                                                >
                                                    <span className={styles.reactionIcon}>{reactionButtonIcon}</span>
                                                </button>
                                                {pickerOpen && (
                                                    <div className={styles.reactionPicker}>
                                                        {Object.entries(REACTION_TYPES)
                                                            .filter(([type]) => type !== "cheers")
                                                            .map(([type, reaction]) => (
                                                                <button
                                                                    key={type}
                                                                    type="button"
                                                                    className={styles.reactionPickerOption}
                                                                    aria-label={reaction.label}
                                                                    title={reaction.label}
                                                                    onClick={() => handleReactionSelect(post.id, type)}
                                                                >
                                                                    <span className={styles.reactionPickerIcon}>{reaction.icon}</span>
                                                                </button>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.cheersCountButton}
                                                onClick={() =>
                                                    setExpandedCheers((prev) => ({
                                                        ...prev,
                                                        [post.id]: !prev[post.id],
                                                    }))
                                                }
                                            >
                                                <span className={styles.reactionStack} aria-hidden="true">
                                                    {reactionIcons.map((icon, index) => (
                                                        <span
                                                            key={`${post.id}-reaction-icon-${index}`}
                                                            className={styles.reactionStackIcon}
                                                            style={{ zIndex: reactionIcons.length - index }}
                                                        >
                                                            {icon}
                                                        </span>
                                                    ))}
                                                </span>
                                                <span>
                                                    {post.toasts.length} reaction{post.toasts.length === 1 ? "" : "s"}
                                                </span>
                                            </button>
                                            {cheersExpanded && post.toasts.length > 0 && (
                                                <div className={styles.cheersList}>
                                                    {post.toasts.map((toast) => (
                                                        <div key={toast.id} className={styles.cheersUser}>
                                                            <strong>{toast.author ? toast.author.username : "Unknown"}</strong>
                                                            <span className={styles.cheersUserReaction}>
                                                                {(REACTION_TYPES[toast.type] || REACTION_TYPES.cheers).icon}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </section>

                                        <section className={`${styles.interactionPanel} ${styles.commentsPanel}`}>
                                            <div className={styles.commentsHeader}>
                                                <h4 className={styles.interactionTitle}>Comments ({comments.length})</h4>
                                            </div>

                                            {hiddenCommentCount > 0 && (
                                                <div className={styles.commentsLoadMore}>
                                                    <button
                                                        type="button"
                                                        className={styles.inlineTextButton}
                                                        onClick={() =>
                                                            setExpandedComments((prev) => ({
                                                                ...prev,
                                                                [post.id]: Math.min(
                                                                    comments.length,
                                                                    (prev[post.id] || COMMENT_PAGE_SIZE) + COMMENT_PAGE_SIZE
                                                                ),
                                                            }))
                                                        }
                                                    >
                                                        Show more comments
                                                    </button>
                                                    <span className={styles.commentsLoadMoreMeta}>
                                                        {hiddenCommentCount} more comment{hiddenCommentCount === 1 ? "" : "s"}
                                                    </span>
                                                </div>
                                            )}

                                            {hiddenCommentCount === 0 && canCollapseComments && (
                                                <div className={styles.commentsLoadMore}>
                                                    <button
                                                        type="button"
                                                        className={styles.inlineTextButton}
                                                        onClick={() =>
                                                            setExpandedComments((prev) => ({
                                                                ...prev,
                                                                [post.id]: COMMENT_PAGE_SIZE,
                                                            }))
                                                        }
                                                    >
                                                        Hide earlier comments
                                                    </button>
                                                </div>
                                            )}

                                            <div className={styles.itemList}>
                                                {visibleComments.map((comment) => {
                                                    const isOwner =
                                                        currentUser && comment.author && currentUser.id === comment.author.id;
                                                    const canDeleteComment = currentUser && (isOwner || currentUser.isAdmin);
                                                    const canEditComment = Boolean(isOwner);
                                                    const draftKey = comment.id;
                                                    const commentMenuOpen = openCommentMenuId === comment.id;
                                                    const commentReaction = (comment.reactions || []).find(
                                                        (reaction) => reaction.author && reaction.author.id === currentUser.id
                                                    );
                                                    const commentReactionIcons = getReactionIcons(comment.reactions || []);
                                                    const commentReactionExpanded = Boolean(expandedCommentReactions[comment.id]);
                                                    const commentPickerOpen = openCommentReactionPickerId === comment.id;

                                                    return (
                                                        <div key={comment.id} className={styles.itemCard}>
                                                            <div className={styles.itemMeta}>
                                                                <strong>{comment.author ? comment.author.username : "Unknown"}</strong>
                                                                <span>{formatTimestamp(comment.updatedAt || comment.createdAt)}</span>
                                                            </div>

                                                            {editingCommentId === comment.id ? (
                                                                <div className={styles.formGrid}>
                                                                    <textarea
                                                                        className={styles.fieldArea}
                                                                        value={commentDrafts[draftKey] || ""}
                                                                        onChange={(event) =>
                                                                            setCommentDrafts((prev) => ({
                                                                                ...prev,
                                                                                [draftKey]: event.target.value,
                                                                            }))
                                                                        }
                                                                    />
                                                                    <div className={styles.formRow}>
                                                                        <button
                                                                            type="button"
                                                                            className={styles.secondaryAction}
                                                                            onClick={() => submitComment(post.id, comment.id)}
                                                                        >
                                                                            Save
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className={styles.ghostAction}
                                                                            onClick={() => setEditingCommentId(null)}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <p className={styles.itemBody}>{comment.body}</p>
                                                            )}

                                                            {canDeleteComment && editingCommentId !== comment.id && (
                                                                <div className={styles.commentMenuWrap} ref={commentMenuOpen ? commentMenuRef : null}>
                                                                    <button
                                                                        type="button"
                                                                        className={styles.commentMenuButton}
                                                                        aria-label="Comment actions"
                                                                        onClick={() =>
                                                                            setOpenCommentMenuId((prev) => (prev === comment.id ? null : comment.id))
                                                                        }
                                                                    >
                                                                        ...
                                                                    </button>
                                                                    {commentMenuOpen && (
                                                                        <div className={styles.postMenu}>
                                                                            {canEditComment && (
                                                                                <button
                                                                                    type="button"
                                                                                    className={styles.postMenuItem}
                                                                                    onClick={() => {
                                                                                        setOpenCommentMenuId(null);
                                                                                        setEditingCommentId(comment.id);
                                                                                        setCommentDrafts((prev) => ({
                                                                                            ...prev,
                                                                                            [draftKey]: comment.body,
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                className={`${styles.postMenuItem} ${styles.postMenuDanger}`}
                                                                                onClick={() => {
                                                                                    setOpenCommentMenuId(null);
                                                                                    runAction(
                                                                                        () =>
                                                                                            deleteComment({
                                                                                                postId: post.id,
                                                                                                commentId: comment.id,
                                                                                            }),
                                                                                        "Comment deleted."
                                                                                    );
                                                                                }}
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div className={styles.commentReactionRow}>
                                                                <div
                                                                    className={styles.reactionControl}
                                                                    ref={commentPickerOpen ? reactionPickerRef : null}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        className={`${styles.reactionToggle} ${commentReaction ? styles.reactionToggleActive : ""} ${styles.commentReactionToggle}`}
                                                                        aria-label={commentReaction ? "Update comment reaction" : "Add cheers reaction to comment"}
                                                                        title="Tap for cheers, hold for more reactions"
                                                                        onMouseDown={() => startCommentReactionHold(comment.id)}
                                                                        onMouseUp={cancelReactionHold}
                                                                        onMouseLeave={cancelReactionHold}
                                                                        onTouchStart={() => startCommentReactionHold(comment.id)}
                                                                        onTouchEnd={cancelReactionHold}
                                                                        onTouchCancel={cancelReactionHold}
                                                                        onClick={() => handleCommentReactionButtonClick(post.id, comment.id)}
                                                                    >
                                                                        <span className={styles.reactionIcon}>
                                                                            {commentReaction
                                                                                ? (REACTION_TYPES[commentReaction.type] || REACTION_TYPES.cheers).icon
                                                                                : REACTION_TYPES.cheers.icon}
                                                                        </span>
                                                                    </button>
                                                                    {commentPickerOpen && (
                                                                        <div className={styles.reactionPicker}>
                                                                            {Object.entries(REACTION_TYPES)
                                                                                .filter(([type]) => type !== "cheers")
                                                                                .map(([type, reaction]) => (
                                                                                    <button
                                                                                        key={`${comment.id}-${type}`}
                                                                                        type="button"
                                                                                        className={styles.reactionPickerOption}
                                                                                        aria-label={reaction.label}
                                                                                        title={reaction.label}
                                                                                        onClick={() => handleCommentReactionSelect(post.id, comment.id, type)}
                                                                                    >
                                                                                        <span className={styles.reactionPickerIcon}>{reaction.icon}</span>
                                                                                    </button>
                                                                                ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className={styles.cheersCountButton}
                                                                    onClick={() =>
                                                                        setExpandedCommentReactions((prev) => ({
                                                                            ...prev,
                                                                            [comment.id]: !prev[comment.id],
                                                                        }))
                                                                    }
                                                                >
                                                                    <span className={styles.reactionStack} aria-hidden="true">
                                                                        {commentReactionIcons.map((icon, index) => (
                                                                            <span
                                                                                key={`${comment.id}-reaction-icon-${index}`}
                                                                                className={styles.reactionStackIcon}
                                                                                style={{ zIndex: commentReactionIcons.length - index }}
                                                                            >
                                                                                {icon}
                                                                            </span>
                                                                        ))}
                                                                    </span>
                                                                    <span>
                                                                        {(comment.reactions || []).length} reaction
                                                                        {(comment.reactions || []).length === 1 ? "" : "s"}
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            {commentReactionExpanded && (comment.reactions || []).length > 0 && (
                                                                <div className={styles.cheersList}>
                                                                    {(comment.reactions || []).map((reaction) => (
                                                                        <div key={reaction.id} className={styles.cheersUser}>
                                                                            <strong>{reaction.author ? reaction.author.username : "Unknown"}</strong>
                                                                            <span className={styles.cheersUserReaction}>
                                                                                {(REACTION_TYPES[reaction.type] || REACTION_TYPES.cheers).icon}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className={styles.formGrid}>
                                                <textarea
                                                    className={styles.fieldArea}
                                                    placeholder="Leave a comment"
                                                    value={commentDrafts[post.id] || ""}
                                                    onChange={(event) =>
                                                        setCommentDrafts((prev) => ({ ...prev, [post.id]: event.target.value }))
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    className={styles.primaryAction}
                                                    onClick={() => submitComment(post.id)}
                                                >
                                                    Add comment
                                                </button>
                                            </div>
                                        </section>
                                    </div>
                                </article>
                            );
                        })}
                        {currentUser && (
                            <div ref={feedSentinelRef} className={styles.feedStatus}>
                                {isLoadingMorePosts
                                    ? "Loading more posts..."
                                    : postPagination.hasMore
                                        ? "Scroll for more posts"
                                        : posts.length > 0
                                            ? "You're all caught up."
                                            : ""}
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default PLApp;
